import { lazy, type ReactElement, Suspense, useMemo } from 'react';
import {
  hasVisibleChartValue,
  hasVisibleStackedChartValue,
} from '@/features/uploads/pages/executionRunsPageCharts';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { ChartEmptyState } from '@/ui/composites';
import { Panel } from '@/ui/composites/Panel';
import { Box, PieChart, Text } from '@/ui/primitives';
import { formatChartDateLabel } from '@/ui/primitives/chartDateLabel';
import { darken } from '@/ui/primitives/colorUtils';
import { useMediaQuery, useTheme } from '@/ui/primitives/hooks';
import { LineChart } from '@/ui/primitives/LineChart';
import { StackedBarChartSkeleton } from '@/ui/primitives/StackedBarChartSkeleton';
import { MetricChartPanel } from './MetricChartPanel';
import { MiniMetricChartHeader } from './MiniMetricChartHeader';
import {
  renderMiniMetricHeaderLoadingState,
  formatStackedStatusChartLabel,
  renderMiniMetricEmptyState,
  renderMiniMetricLoadingState,
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
  renderStatusChartLegendLoadingState,
  renderStatusPieLoadingState,
} from './StackedMetricsStatusChartSection.states';
import { ChartToolbarPanel, StatusChartPanel } from './StatusChartPanel';
import { StatusMetricCardsGrid } from './StatusMetricCardsGrid';
import {
  type MetricsStatusChartPanelModelToolbarState,
  type MetricsStatusChartPanelDisplayModel,
  useMetricsStatusChartPanelModel,
} from './useMetricsStatusChartPanelModel';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsScope } from '@/features/metrics/metricsHelpers';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

const StackedBarChart = lazy(async () => ({
  default: (await import('@/ui/primitives/StackedBarChart')).StackedBarChart,
}));
const darkenColor = darken as (color: string, coefficient: number) => string;
export type StackedMetricsStatusChartSectionProps<TStatus extends string> = {
  title?: string;
  searchAriaLabel: string;
  noRowsBaseText: string;
  scope: MetricsScope;
  projectId: string | null;
  availableTags: string[];
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel?: string;
  metricCards: MetricCardItem[];
  loading?: boolean;
  statusCardSkeletonCount?: number;
  selectedStatusIds?: string[];
  onStatusToggle?: (statusId: string) => void;
  toolbarState?: MetricsStatusChartPanelModelToolbarState<TStatus>;
  modelOverride?: MetricsStatusChartPanelDisplayModel;
  suppressAnimation: boolean;
  showPoints?: boolean;
};
export const StackedMetricsStatusChartSection = function StackedMetricsStatusChartSection<
  TStatus extends string,
>({
  title = 'Status',
  searchAriaLabel,
  noRowsBaseText,
  scope,
  projectId,
  availableTags,
  statusOptions,
  statusFilterLabel = 'Status',
  metricCards,
  loading = false,
  statusCardSkeletonCount,
  selectedStatusIds: _selectedStatusIds,
  onStatusToggle: _onStatusToggle,
  toolbarState,
  modelOverride,
  suppressAnimation,
  showPoints: _showPoints = false,
}: StackedMetricsStatusChartSectionProps<TStatus>): ReactElement {
  const miniMetricEmptyTitle = noRowsBaseText;
  const theme = useTheme();
  const isNarrowChartLayout = useMediaQuery(theme.breakpoints.down('lg'));
  const isTwoColumnChartLayout = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const metricsColumnHeight = 300;
  const sharedTwoColumnPanelHeight = 320;
  const piePanelHeight = isTwoColumnChartLayout
    ? sharedTwoColumnPanelHeight
    : isNarrowChartLayout
      ? 248
      : metricsColumnHeight;
  const miniPanelsRowGapPx = 16;
  const stackedSectionBaseRowHeight = (metricsColumnHeight - miniPanelsRowGapPx * 2) / 3;

  const statusPanel = {
    height: isTwoColumnChartLayout
      ? sharedTwoColumnPanelHeight
      : isNarrowChartLayout
        ? 288
        : metricsColumnHeight,
    chartHeight: isTwoColumnChartLayout
      ? 268
      : isNarrowChartLayout
        ? 244
        : metricsColumnHeight - 42,
    headerHeight: 28,
    headerToChartGap: 24,
    panelPadding: 'md' as const,
    panelContentGap: 'md' as const,
    headerTitlePaddingLeft: s('md'),
    headerControlsPaddingRight: s('md'),
    yAxisWidth: 40,
    yAxisAutoWidth: true,
    yAxisAutoWidthPadding: 6,
    axisStrokeWidth: 0.75,
    barCornerRadius: 6,
    yAxisLabelOffset: -2,
    hideXAxisTicks: false,
    xAxisLabelOffset: 3,
    chartMargins: {
      left: -10,
      right: 16,
      top: 0,
      bottom: isNarrowChartLayout ? 14 : 18,
    },
  };
  const miniPanels = {
    height: stackedSectionBaseRowHeight,
    headerHeight: 28,
    chartHeight: 46,
    columnGap: s('lg'),
    rowGap: s('xl'),
    panelPaddingTop: s('sm'),
    panelPaddingBottom: s('none'),
    headerPaddingX: s('lg'),
    headerPaddingTop: s('none'),
    headerPaddingBottom: s('none'),
    contentPaddingX: s('md'),
    contentPaddingTop: s('sm'),
    contentPaddingBottom: s('none'),
    chartWrapperPaddingX: s('none'),
    chartWrapperPaddingTop: s('none'),
    chartWrapperPaddingBottom: s('none'),
    yAxisWidth: 0,
    axisStrokeWidth: 0.75,
    yDomainPaddingTopRatio: 0.08,
    yDomainPaddingBottomRatio: 0,
    chartMargins: {
      left: 6,
      right: 6,
      top: 0,
      bottom: -8,
    },
  };
  const sectionLayout = {
    gap: s('xl'),
    rowGap: s('xl'),
    toolbarGap: s('2xl'),
    bottomGap: s('none'),
  };
  const pieChartVisualHeight = isNarrowChartLayout
    ? piePanelHeight - miniPanels.headerHeight - (isTwoColumnChartLayout ? 78 : 52)
    : piePanelHeight - miniPanels.headerHeight - 72;
  const statusChartVisualHeight = statusPanel.chartHeight + 12;

  const internalModel = useMetricsStatusChartPanelModel<TStatus>({
    noRowsBaseText,
    scope,
    projectId,
    externalToolbarState: toolbarState,
    disabled: modelOverride !== undefined,
  });
  const effectiveToolbarState = toolbarState ?? internalModel.toolbarState;
  const model = modelOverride ?? internalModel;
  const statusChartData = model.statusChartData;
  const statusPieData = useMemo(() => {
    if (!statusChartData) return [];
    return statusChartData.series
      .map((seriesItem) => {
        const total = statusChartData.data.reduce((sum, datum) => {
          const value = datum[seriesItem.key];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        const explicitColor = typeof seriesItem.color === 'string' ? seriesItem.color : undefined;
        const colorKey = typeof seriesItem.colorKey === 'string' ? seriesItem.colorKey : undefined;
        const indexedColor = colorKey
          ? uiTokens.statusColors[colorKey as keyof typeof uiTokens.statusColors]
          : undefined;
        const tokenColor = typeof indexedColor === 'string' ? indexedColor : undefined;
        const baseColor = explicitColor ?? tokenColor;
        const resolvedColor =
          typeof baseColor === 'string'
            ? isLightTheme(theme)
              ? baseColor
              : darkenColor(baseColor, 0.26)
            : undefined;
        return {
          name: seriesItem.label,
          value: total,
          color: resolvedColor,
        };
      })
      .filter((entry) => entry.value > 0);
  }, [statusChartData, theme]);
  const comparisonWindowLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = effectiveToolbarState;
    if (fromDate || toDate) {
      return 'All';
    }
    if (dateQuickFilter === '7d') return 'Last 7 days';
    if (dateQuickFilter === '14d') return 'Last 14 days';
    if (dateQuickFilter === '30d') return 'Last 30 days';
    return 'Latest';
  }, [effectiveToolbarState]);
  const metricChart = (summary: ReactElement, chartNode: ReactElement): ReactElement => (
    <MetricChartPanel
      header={summary}
      height={miniPanels.height}
      headerHeight={miniPanels.headerHeight}
      panelPaddingTop={miniPanels.panelPaddingTop}
      panelPaddingBottom={miniPanels.panelPaddingBottom}
      headerPaddingX={miniPanels.headerPaddingX}
      headerPaddingTop={miniPanels.headerPaddingTop}
      headerPaddingBottom={miniPanels.headerPaddingBottom}
      contentPaddingX={miniPanels.contentPaddingX}
      contentPaddingTop={miniPanels.contentPaddingTop}
      contentPaddingBottom={miniPanels.contentPaddingBottom}
    >
      <Box
        sx={{
          px: miniPanels.chartWrapperPaddingX,
          pt: miniPanels.chartWrapperPaddingTop,
          pb: miniPanels.chartWrapperPaddingBottom,
        }}
      >
        {chartNode}
      </Box>
    </MetricChartPanel>
  );
  const statusLegend =
    statusPieData.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
        }}
      >
        {statusPieData.map((entry) => (
          <Box
            key={entry.name}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
            }}
          >
            <Box
              sx={{
                width: s('md'),
                height: s('md'),
                borderRadius: s('xxs'),
                backgroundColor: entry.color ?? 'text.secondary',
                flexShrink: 0,
              }}
            />
            <Text
              role="bodyMuted"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
              }}
            >
              {entry.name}
            </Text>
          </Box>
        ))}
      </Box>
    ) : null;

  const statusChartLegend =
    statusPieData.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: isNarrowChartLayout ? 'center' : 'flex-end',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
          pr: isNarrowChartLayout ? s('none') : s('2xl'),
          minWidth: 0,
        }}
      >
        {statusPieData.map((entry) => (
          <Box
            key={entry.name}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
            }}
          >
            <Box
              sx={{
                width: s('md'),
                height: s('md'),
                borderRadius: s('xxs'),
                backgroundColor: entry.color ?? 'text.secondary',
                flexShrink: 0,
              }}
            />
            <Text
              role="bodyMuted"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
              }}
            >
              {entry.name}
            </Text>
          </Box>
        ))}
      </Box>
    ) : null;
  const resolvedStatusChartLegend = loading
    ? renderStatusChartLegendLoadingState()
    : statusChartLegend;
  const hasMetricChartValue = {
    passRate: hasVisibleChartValue(model.passRateChartData, 'passRate'),
    frequency: hasVisibleChartValue(model.frequencyChartData, 'frequency'),
    duration: hasVisibleChartValue(model.durationChartData, 'duration'),
  };
  const statusEmptyTitle = effectiveToolbarState.noRowsText;

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'hidden',
        pb: sectionLayout.bottomGap,
      }}
    >
      <Box
        sx={{
          width: '100%',
          mb: sectionLayout.toolbarGap,
        }}
      >
        <ChartToolbarPanel<TStatus>
          title=""
          searchAriaLabel={searchAriaLabel}
          panelPadding="xs"
          panelPaddingX={s('md')}
          onDebouncedSearchChange={effectiveToolbarState.setSearch}
          filterStatuses={effectiveToolbarState.selectedStatuses}
          statusOptions={statusOptions}
          onFilterStatusesChange={effectiveToolbarState.setSelectedStatuses}
          filterTags={effectiveToolbarState.selectedTags}
          onFilterTagsChange={effectiveToolbarState.setSelectedTags}
          dateQuickFilter={effectiveToolbarState.dateQuickFilter}
          onDateQuickFilterChange={effectiveToolbarState.onDateQuickFilterChange}
          fromDate={effectiveToolbarState.fromDate}
          toDate={effectiveToolbarState.toDate}
          onFromDateChange={(next) => {
            effectiveToolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
          }}
          onToDateChange={(next) => {
            effectiveToolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
          }}
          availableTags={availableTags}
          statusFilterLabel={statusFilterLabel}
          headerTitlePaddingLeft={s('none')}
          headerControlsPaddingRight={s('none')}
        />
      </Box>
      <Box sx={{ mb: sectionLayout.toolbarGap }}>
        <StatusMetricCardsGrid
          metricCards={metricCards}
          loading={loading}
          skeletonCount={statusCardSkeletonCount}
          comparisonWindowLabel={comparisonWindowLabel}
          resolveStatusCardIcon={(statusId) => resolveStackedStatusCardIcon(statusId, scope)}
          resolveStatusCardIconColor={resolveStackedStatusCardIconColor}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateAreas: {
            xs: '"pie" "status" "mini"',
            sm: '"pie status" "mini mini"',
            lg: '"pie status mini"',
          },
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'minmax(260px, 1.4fr) minmax(0, 2.8fr)',
            lg: 'minmax(220px, 1.5fr) minmax(0, 5fr) minmax(300px, 2.8fr)',
          },
          columnGap: sectionLayout.gap,
          rowGap: sectionLayout.rowGap,
          alignItems: 'start',
        }}
      >
        <Panel
          padding="none"
          gap="none"
          contentGap="none"
          sx={{
            gridArea: 'pie',
            minWidth: 0,
            height: piePanelHeight,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: miniPanels.headerPaddingX,
                pt: s('lg'),
                pb: miniPanels.headerPaddingBottom,
                minHeight: `${miniPanels.headerHeight}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              <Text
                role="label"
                sx={{
                  color: 'text.secondary',
                  fontSize: 'subtitle1.fontSize',
                  lineHeight: 1.4,
                }}
              >
                Status Breakdown
              </Text>
            </Box>
            <Box
              sx={{
                px: s('lg'),
                pb: s('lg'),
                flex: 1,
                minHeight: 0,
                display: 'grid',
                alignItems: 'stretch',
                boxSizing: 'border-box',
              }}
            >
              {loading ? (
                renderStatusPieLoadingState(pieChartVisualHeight)
              ) : statusPieData.length > 0 ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: s('xs'),
                    minHeight: 0,
                  }}
                >
                  <PieChart
                    data={statusPieData}
                    height={pieChartVisualHeight}
                    innerRadius={38}
                    showLegend={false}
                  />
                  {statusLegend}
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: pieChartVisualHeight,
                    minHeight: pieChartVisualHeight,
                    display: 'grid',
                    placeItems: 'center',
                    transform: `translateY(${statusPanel.headerToChartGap}px)`,
                  }}
                >
                  <ChartEmptyState title={miniMetricEmptyTitle} />
                </Box>
              )}
            </Box>
          </Box>
        </Panel>
        <Box sx={{ gridArea: 'status', minWidth: 0 }}>
          <StatusChartPanel<TStatus>
            title={title}
            showToolbarControls={false}
            searchAriaLabel={searchAriaLabel}
            height={statusPanel.height}
            chartHeight={statusPanel.chartHeight}
            panelPadding={statusPanel.panelPadding}
            panelContentGap={statusPanel.panelContentGap}
            headerHeight={statusPanel.headerHeight}
            headerTitlePaddingLeft={statusPanel.headerTitlePaddingLeft}
            headerControlsPaddingRight={statusPanel.headerControlsPaddingRight}
            emptyTitle={statusEmptyTitle}
            emptyBody="Try adjusting filters or search."
            onDebouncedSearchChange={effectiveToolbarState.setSearch}
            filterStatuses={effectiveToolbarState.selectedStatuses}
            statusOptions={statusOptions}
            onFilterStatusesChange={effectiveToolbarState.setSelectedStatuses}
            filterTags={effectiveToolbarState.selectedTags}
            onFilterTagsChange={effectiveToolbarState.setSelectedTags}
            dateQuickFilter={effectiveToolbarState.dateQuickFilter}
            onDateQuickFilterChange={effectiveToolbarState.onDateQuickFilterChange}
            fromDate={effectiveToolbarState.fromDate}
            toDate={effectiveToolbarState.toDate}
            onFromDateChange={(next) => {
              effectiveToolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
            }}
            onToDateChange={(next) => {
              effectiveToolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
            }}
            availableTags={availableTags}
            statusFilterLabel={statusFilterLabel}
            headerRightSlot={resolvedStatusChartLegend}
          >
            {loading || model.showStatusChartSkeleton ? (
              <StackedBarChartSkeleton height={statusChartVisualHeight} />
            ) : statusChartData &&
              hasVisibleStackedChartValue(statusChartData.data, statusChartData.series) ? (
              <Suspense fallback={<StackedBarChartSkeleton height={statusChartVisualHeight} />}>
                <StackedBarChart
                  data={statusChartData.data}
                  series={statusChartData.series}
                  height={statusChartVisualHeight}
                  xAxisLabelFormatter={formatStackedStatusChartLabel}
                  tooltipLabelFormatter={formatChartDateLabel}
                  marginLeft={statusPanel.chartMargins.left}
                  marginRight={statusPanel.chartMargins.right}
                  marginTop={statusPanel.headerToChartGap}
                  marginBottom={statusPanel.chartMargins.bottom}
                  yAxisWidth={statusPanel.yAxisWidth}
                  yAxisAutoWidth={statusPanel.yAxisAutoWidth}
                  yAxisAutoWidthPadding={statusPanel.yAxisAutoWidthPadding}
                  axisStrokeWidth={statusPanel.axisStrokeWidth}
                  barCornerRadius={statusPanel.barCornerRadius}
                  yAxisLabelOffset={statusPanel.yAxisLabelOffset}
                  hideXAxisTicks={statusPanel.hideXAxisTicks}
                  xAxisLabelOffset={statusPanel.xAxisLabelOffset}
                  showHorizontalGrid={false}
                  hideXAxisLine={false}
                  skipAnimation={suppressAnimation}
                />
              </Suspense>
            ) : (
              <Box
                sx={{
                  height: statusChartVisualHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={statusEmptyTitle} body="Try adjusting filters or search." />
              </Box>
            )}
          </StatusChartPanel>
        </Box>
        <Box
          sx={{
            gridArea: 'mini',
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
              lg: 'minmax(0, 1fr)',
            },
            columnGap: miniPanels.columnGap,
            rowGap: miniPanels.rowGap,
            width: '100%',
            minWidth: 0,
          }}
        >
          {metricChart(
            loading ? (
              renderMiniMetricHeaderLoadingState()
            ) : (
              <MiniMetricChartHeader
                title="Pass Rate"
                summary={hasMetricChartValue.passRate ? model.passRateSummary : null}
              />
            ),
            loading ? (
              renderMiniMetricLoadingState(miniPanels.chartHeight)
            ) : model.passRateChartData && hasMetricChartValue.passRate ? (
              <LineChart
                data={model.passRateChartData}
                series={[{ key: 'passRate', label: '', color: uiTokens.statusColors.green }]}
                height={miniPanels.chartHeight}
                marginLeft={miniPanels.chartMargins.left}
                marginRight={miniPanels.chartMargins.right}
                marginTop={miniPanels.chartMargins.top}
                marginBottom={miniPanels.chartMargins.bottom}
                yAxisWidth={miniPanels.yAxisWidth}
                axisStrokeWidth={miniPanels.axisStrokeWidth}
                showLegend={false}
                showHorizontalGrid={false}
                hideXAxisLine={false}
                yDomainPaddingTopRatio={miniPanels.yDomainPaddingTopRatio}
                yDomainPaddingBottomRatio={miniPanels.yDomainPaddingBottomRatio}
                hideXAxisLabels
                hideYAxisLabels
                hideXAxisTicks
                hideYAxisTicks
                showPoints={model.passRateChartData.length === 1}
              />
            ) : (
              renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
            ),
          )}
          {metricChart(
            loading ? (
              renderMiniMetricHeaderLoadingState()
            ) : (
              <MiniMetricChartHeader
                title="Frequency"
                summary={hasMetricChartValue.frequency ? model.frequencySummary : null}
              />
            ),
            loading ? (
              renderMiniMetricLoadingState(miniPanels.chartHeight)
            ) : model.frequencyChartData && hasMetricChartValue.frequency ? (
              <LineChart
                data={model.frequencyChartData}
                series={[
                  {
                    key: 'frequency',
                    label: scope === 'executionRun' ? 'Runs' : '',
                    color: 'var(--color-pink)',
                  },
                ]}
                height={miniPanels.chartHeight}
                marginLeft={miniPanels.chartMargins.left}
                marginRight={miniPanels.chartMargins.right}
                marginTop={miniPanels.chartMargins.top}
                marginBottom={miniPanels.chartMargins.bottom}
                yAxisWidth={miniPanels.yAxisWidth}
                axisStrokeWidth={miniPanels.axisStrokeWidth}
                showLegend={false}
                showHorizontalGrid={false}
                hideXAxisLine={false}
                yDomainPaddingTopRatio={miniPanels.yDomainPaddingTopRatio}
                yDomainPaddingBottomRatio={miniPanels.yDomainPaddingBottomRatio}
                hideXAxisLabels
                hideYAxisLabels
                hideXAxisTicks
                hideYAxisTicks
                showPoints={model.frequencyChartData.length === 1}
              />
            ) : (
              renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
            ),
          )}
          {metricChart(
            loading ? (
              renderMiniMetricHeaderLoadingState()
            ) : (
              <MiniMetricChartHeader
                title="Avg. Duration"
                summary={hasMetricChartValue.duration ? model.durationSummary : null}
              />
            ),
            loading ? (
              renderMiniMetricLoadingState(miniPanels.chartHeight)
            ) : model.durationChartData && hasMetricChartValue.duration ? (
              <LineChart
                data={model.durationChartData}
                series={[{ key: 'duration', label: '' }]}
                height={miniPanels.chartHeight}
                marginLeft={miniPanels.chartMargins.left}
                marginRight={miniPanels.chartMargins.right}
                marginTop={miniPanels.chartMargins.top}
                marginBottom={miniPanels.chartMargins.bottom}
                yAxisWidth={miniPanels.yAxisWidth}
                axisStrokeWidth={miniPanels.axisStrokeWidth}
                showLegend={false}
                showHorizontalGrid={false}
                hideXAxisLine={false}
                yDomainPaddingTopRatio={miniPanels.yDomainPaddingTopRatio}
                yDomainPaddingBottomRatio={miniPanels.yDomainPaddingBottomRatio}
                hideXAxisLabels
                hideYAxisLabels
                hideXAxisTicks
                hideYAxisTicks
                showPoints={model.durationChartData.length === 1}
              />
            ) : (
              renderMiniMetricEmptyState(miniMetricEmptyTitle, miniPanels.chartHeight)
            ),
          )}
        </Box>
      </Box>
    </Box>
  );
};
